/**
 * `<caos-file-field>` — a file field. (Catalog: Fields & input › FileField — attach files to a record, with each one's own progress and its own failure.)
 *
 * The editor primitive behind the `File` storage kind, and the control the Report a Bug
 * page's `attachment` declaration resolves to. Files arrive by picker, by drop, or by
 * paste — a screenshot is usually pasted, so paste is a first-class way in rather than a
 * convenience on top of the picker.
 *
 * TWO RULES THIS COMPONENT EXISTS TO KEEP (CAOS-596), both of them defects in the
 * hand-rolled control this replaces:
 *
 * 1. BUSY IS NOT DISABLED. A file in flight sets `aria-busy` on THAT FILE'S row and
 *    nothing else. The picker stays live, the other rows stay live, and an in-flight row
 *    keeps its own remove button — cancelling a slow upload is the single most likely
 *    thing a person wants while one is running, and disabling the control to signal
 *    progress takes exactly that away. `disabled` on this element still means disabled;
 *    it is never borrowed to mean "working".
 *
 * 2. A REJECTED FILE SAYS SO. Every way a file can fail to be accepted — wrong type,
 *    too large, unreadable, or an upload that threw — puts a message where the person is
 *    looking, on the row for that file or in the field's own message line when there is no
 *    row to hang it on. The control it replaces filtered to images with
 *    `if (imgs.length === 0) return;`, so dropping a PDF on it did nothing and said
 *    nothing. Silence is the failure mode this is written against.
 *
 * TRANSPORT IS INJECTED, AND ITS DEFAULT IS THE INCUMBENT. {@link CaosFileField.uploader}
 * takes a file and returns where it now lives. The default reads it into a data URL, which
 * is what the Report a Bug intake does today and therefore what keeps working when this
 * lands. It is deliberately NOT a kernel upload: `files/upload-session.ts` is built and
 * reachable by no route (CAOS-508), and the only `BlobStore` in the kernel is
 * `InMemoryBlobStore`, which holds nothing across an isolate recycle. Routing bytes there
 * would move screenshots that currently survive in the record into a store that forgets.
 * When a durable store and its route exist, they replace this one function.
 *
 * Styling seams: `::part(control)`, `::part(dropzone)`, `::part(label)`, `::part(list)`,
 * `::part(item)`, `::part(message)`. Fires a bubbling, composed `caos-file-change` whose
 * detail is the accepted files; `value` reads them back.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

/** Where one accepted file ended up, and what it is called. */
export interface CaosFileValue {
  readonly name: string;
  readonly url: string;
  readonly size: number;
  readonly type: string;
}

/**
 * The transport seam. Resolves to the location the bytes now live at. Rejecting is a
 * normal outcome and surfaces on that file's row — it must not be swallowed.
 */
export type CaosFileUploader = (file: File) => Promise<string>;

/** Where a single file is in its life. `failed` carries the reason a person reads. */
type ItemState = 'uploading' | 'done' | 'failed';

interface Item {
  readonly file: File;
  state: ItemState;
  url: string;
  reason: string;
}

const sheet = makeSheet(`
  :host { display: block; }

  .control { display: flex; flex-direction: column; gap: var(--caos-space-1); }

  .label {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    font-weight: 600;
    color: var(--caos-color-text-muted);
  }
  .label:empty { display: none; }
  /* CAOS-1697 — there is DELIBERATELY no rule here for the suppressed caption. base-element.ts
     already adopts [hidden] { display: none !important } into every component's shadow root
     (CAOS-581), which is stronger than anything this sheet could add without !important and is the
     mechanism the rest of the library is tested against. A second, weaker copy of it here would
     read as the thing doing the work and would send the next person to the wrong place.
     (No backticks in this comment: the sheet is a template literal, and one ends it.) */
  .req { color: var(--caos-color-danger); margin-left: 2px; }

  /* The drop target doubles as the picker's affordance. */
  .dropzone {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    min-height: 40px;
    padding: var(--caos-space-3) 14px;
    border: 1px dashed var(--caos-color-border);
    border-radius: var(--caos-radius);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    cursor: pointer;
    transition: border-color 0.12s ease, background 0.12s ease;
  }
  .dropzone:hover:not([aria-disabled='true']) {
    border-color: color-mix(in srgb, var(--caos-color-accent) 45%, var(--caos-color-border));
  }
  .dropzone:focus-visible {
    outline: none;
    border-color: var(--caos-color-border-strong);
    box-shadow: inset 0 0 0 100px var(--caos-accent-wash);
  }
  .dropzone[data-over='true'] {
    border-color: var(--caos-color-accent);
    background: color-mix(in srgb, var(--caos-color-accent) 6%, var(--caos-color-surface-2));
  }
  .dropzone[aria-disabled='true'] { cursor: not-allowed; opacity: var(--caos-state-disabled-opacity); }

  :host([invalid]) .dropzone { border-color: var(--caos-color-danger); }

  /* The native input is the real picker; the dropzone is its visible face. */
  input[type='file'] {
    position: absolute;
    width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }

  .list { display: flex; flex-direction: column; gap: var(--caos-space-1); margin: 0; padding: 0; list-style: none; }
  .list:empty { display: none; }

  .item {
    display: flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: var(--caos-space-2) var(--caos-space-3);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    font-family: var(--caos-font);
    font-size: var(--caos-text-row);
    color: var(--caos-color-text);
  }
  .item[data-state='failed'] {
    border-color: var(--caos-color-danger);
    background: var(--caos-color-danger-bg);
  }

  .thumb {
    width: 32px; height: 32px;
    flex: 0 0 auto;
    object-fit: cover;
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface-2);
  }

  .meta { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
  .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .sub { font-size: var(--caos-text-label); color: var(--caos-color-text-muted); }
  .item[data-state='failed'] .sub { color: var(--caos-color-danger-text); }

  /* The busy signal, on the row that is busy. */
  .spinner {
    width: 14px; height: 14px;
    flex: 0 0 auto;
    border-radius: 50%;
    border: 2px solid color-mix(in srgb, var(--caos-color-accent) 30%, transparent);
    border-top-color: var(--caos-color-accent);
    animation: caos-file-spin 0.7s linear infinite;
  }
  @keyframes caos-file-spin { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) {
    .spinner { animation: none; border-top-color: var(--caos-color-accent); }
  }

  .act {
    flex: 0 0 auto;
    border: 0;
    background: none;
    padding: 2px 6px;
    border-radius: var(--caos-radius-sm);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font);
    font-size: var(--caos-text-label);
    cursor: pointer;
  }
  .act:hover { color: var(--caos-color-text); background: var(--caos-color-surface-2); }
  .act:focus-visible { outline: none; box-shadow: inset 0 0 0 100px var(--caos-accent-wash); }

  .message { font-family: var(--caos-font); font-size: var(--caos-text-label); color: var(--caos-color-danger); }
  .message:empty { display: none; }
`);

/** Read a file into a data URL — the incumbent transport, kept as the default. */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // The control this replaces had no onerror at all, so an unreadable file was silent.
    reader.onerror = () => reject(new Error('could not be read'));
    reader.onabort = () => reject(new Error('reading was interrupted'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
}

/** Bytes, in the units a person reads. */
function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Whether a file matches an `accept` list, using the same grammar the native input does
 * (`.ext`, `type/subtype`, `type/*`). Written out rather than delegated because the
 * picker enforces `accept` for itself and drop and paste do not — the two ways in that
 * the incumbent control let through and then silently discarded.
 */
function matchesAccept(file: File, accept: string): boolean {
  const patterns = accept.split(',').map((p) => p.trim().toLowerCase()).filter(Boolean);
  if (patterns.length === 0) return true;
  const type = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return patterns.some((p) => {
    if (p.startsWith('.')) return name.endsWith(p);
    if (p.endsWith('/*')) return type.startsWith(p.slice(0, -1));
    return type === p;
  });
}

export class CaosFileField extends CaosElement {
  static get observedAttributes(): string[] {
    return [
      'label',
      'label-hidden',
      'required',
      'disabled',
      'invalid',
      'message',
      'accept',
      'multiple',
      'max-bytes',
    ];
  }

  private readonly labelWrap: HTMLLabelElement;
  private readonly labelEl: HTMLSpanElement;
  private readonly reqEl: HTMLSpanElement;
  private readonly dropzone: HTMLDivElement;
  private readonly hint: HTMLSpanElement;
  private readonly input: HTMLInputElement;
  private readonly listEl: HTMLUListElement;
  private readonly messageEl: HTMLParagraphElement;

  private items: Item[] = [];
  /** Set by whoever mounts the field; the default keeps the incumbent behaviour. */
  public uploader: CaosFileUploader = readAsDataUrl;

  constructor() {
    super(sheet);

    const control = document.createElement('div');
    control.className = 'control';
    control.setAttribute('part', 'control');

    const label = document.createElement('label');
    label.className = 'label';
    label.setAttribute('part', 'label');
    this.labelWrap = label;
    this.labelEl = document.createElement('span');
    this.reqEl = document.createElement('span');
    this.reqEl.className = 'req';
    this.reqEl.textContent = '*';
    this.reqEl.hidden = true;
    label.append(this.labelEl, this.reqEl);

    this.input = document.createElement('input');
    this.input.type = 'file';

    this.dropzone = document.createElement('div');
    this.dropzone.className = 'dropzone';
    this.dropzone.setAttribute('part', 'dropzone');
    this.dropzone.tabIndex = 0;
    this.dropzone.setAttribute('role', 'button');
    this.hint = document.createElement('span');
    this.dropzone.append(this.hint);

    this.listEl = document.createElement('ul');
    this.listEl.className = 'list';
    this.listEl.setAttribute('part', 'list');

    this.messageEl = document.createElement('p');
    this.messageEl.className = 'message';
    this.messageEl.setAttribute('part', 'message');
    // A rejection announced only on repaint is a rejection a screen reader misses.
    this.messageEl.setAttribute('role', 'status');
    this.messageEl.setAttribute('aria-live', 'polite');

    control.append(label, this.dropzone, this.input, this.listEl, this.messageEl);
    this.root.append(control);

    this.dropzone.addEventListener('click', () => this.openPicker());
    this.dropzone.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Enter' || k === ' ') {
        e.preventDefault();
        this.openPicker();
      }
    });

    this.input.addEventListener('change', () => {
      this.accept(Array.from(this.input.files ?? []));
      // Clearing lets the same file be picked twice running.
      this.input.value = '';
    });

    this.dropzone.addEventListener('dragover', (e) => {
      if (this.isDisabled) return;
      e.preventDefault();
      this.dropzone.dataset.over = 'true';
    });
    this.dropzone.addEventListener('dragleave', () => {
      delete this.dropzone.dataset.over;
    });
    this.dropzone.addEventListener('drop', (e) => {
      delete this.dropzone.dataset.over;
      if (this.isDisabled) return;
      e.preventDefault();
      this.accept(Array.from((e as DragEvent).dataTransfer?.files ?? []));
    });

    // Paste is how a screenshot actually arrives.
    this.addEventListener('paste', (e) => {
      if (this.isDisabled) return;
      const items = (e as ClipboardEvent).clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const it of Array.from(items)) {
        if (it.kind !== 'file') continue;
        const f = it.getAsFile();
        if (f) files.push(f);
      }
      if (files.length > 0) {
        e.preventDefault();
        this.accept(files);
      }
    });
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /** The files that are actually attached — `done` only. An upload in flight is not a value yet. */
  get value(): readonly CaosFileValue[] {
    return this.items
      .filter((i) => i.state === 'done')
      .map((i) => ({ name: i.file.name, url: i.url, size: i.file.size, type: i.file.type }));
  }

  /** Whether anything is still in flight — the field-level read of the per-row busy state. */
  get busy(): boolean {
    return this.items.some((i) => i.state === 'uploading');
  }

  override focus(options?: FocusOptions): void {
    this.dropzone.focus(options);
  }

  /**
   * Forget everything attached. For a form that has just been submitted and is being reused —
   * without it the next report opens carrying the last one's screenshots. Any upload still in
   * flight is orphaned deliberately: `send` checks the item is still held before recording a
   * result, so a late resolve cannot put a row back after this.
   */
  clear(): void {
    this.items = [];
    this.messageEl.textContent = '';
    this.render();
    this.emit();
  }

  private get isDisabled(): boolean {
    return this.hasAttribute('disabled');
  }

  private openPicker(): void {
    if (this.isDisabled) return;
    this.input.click();
  }

  /**
   * Take a batch in. Each file is judged on its own: one rejected file never discards the
   * batch, and a rejection always says which file and why.
   */
  private accept(files: File[]): void {
    if (files.length === 0) return;
    const accept = this.getAttribute('accept') ?? '';
    const maxBytes = Number(this.getAttribute('max-bytes') ?? '') || 0;
    const multiple = this.hasAttribute('multiple');

    const refusals: string[] = [];
    const taking: File[] = [];

    for (const f of files) {
      if (accept && !matchesAccept(f, accept)) {
        refusals.push(`${f.name || 'that file'} is not an accepted type`);
        continue;
      }
      if (maxBytes > 0 && f.size > maxBytes) {
        refusals.push(`${f.name || 'that file'} is ${humanSize(f.size)}, over the ${humanSize(maxBytes)} limit`);
        continue;
      }
      taking.push(f);
    }

    // A single-file field replaces rather than silently ignoring the newer pick — but a BATCH is
    // the case that made this its own branch. The picker honours `multiple` for itself; drop and
    // paste do not, so two valid files can arrive here at once, and keeping the last one quietly
    // is the same silence this component exists to refuse. The ones not kept are named.
    if (!multiple) {
      const last = taking.pop();
      for (const dropped of taking) {
        refusals.push(`${dropped.name || 'that file'} was not kept — only one file may be attached here`);
      }
      taking.length = 0;
      if (last) {
        this.items = [];
        taking.push(last);
      }
    }

    this.messageEl.textContent = refusals.join('. ');

    for (const f of taking) {
      const item: Item = { file: f, state: 'uploading', url: '', reason: '' };
      this.items.push(item);
      void this.send(item);
    }
    this.render();
    // A batch that was entirely refused still changed what the field says about itself.
    if (taking.length === 0) this.emit();
  }

  /** Run one file through the transport seam and record what happened to it. */
  private async send(item: Item): Promise<void> {
    try {
      const url = await this.uploader(item.file);
      // A row removed, or a field cleared, while this was in flight. Being accurate about what
      // this check does and does not do, because it is easy to over-credit: the item cannot come
      // BACK either way — `items` is the only thing `render` and `value` read, and an orphaned
      // item is not in it. What this prevents is the visible consequence of finishing anyway: a
      // pointless repaint and, worse, a `caos-file-change` announcing a change that did not
      // happen, which a form reading `busy` or `value` off that event acts on.
      if (!this.items.includes(item)) return;
      item.url = url;
      item.state = 'done';
    } catch (err) {
      if (!this.items.includes(item)) return;
      item.state = 'failed';
      item.reason = err instanceof Error && err.message ? err.message : 'could not be uploaded';
    }
    this.render();
    this.emit();
  }

  private dropItem(item: Item): void {
    this.items = this.items.filter((i) => i !== item);
    this.render();
    this.emit();
  }

  private retry(item: Item): void {
    item.state = 'uploading';
    item.reason = '';
    this.render();
    void this.send(item);
  }

  private emit(): void {
    this.dispatchEvent(
      new CustomEvent('caos-file-change', {
        bubbles: true,
        composed: true,
        detail: { value: this.value, busy: this.busy },
      }),
    );
  }

  private render(): void {
    this.listEl.replaceChildren();
    for (const item of this.items) {
      const li = document.createElement('li');
      li.className = 'item';
      li.setAttribute('part', 'item');
      li.dataset.state = item.state;
      // The busy state lives HERE, on the file that is busy — not on the field.
      if (item.state === 'uploading') li.setAttribute('aria-busy', 'true');

      if (item.state === 'uploading') {
        const spin = document.createElement('span');
        spin.className = 'spinner';
        spin.setAttribute('part', 'spinner');
        li.append(spin);
      } else if (item.state === 'done' && item.file.type.startsWith('image/') && item.url) {
        const img = document.createElement('img');
        img.className = 'thumb';
        img.src = item.url;
        img.alt = item.file.name;
        li.append(img);
      }

      const meta = document.createElement('div');
      meta.className = 'meta';
      const name = document.createElement('span');
      name.className = 'name';
      name.textContent = item.file.name || 'Attachment';
      const sub = document.createElement('span');
      sub.className = 'sub';
      sub.textContent =
        item.state === 'uploading'
          ? 'Uploading…'
          : item.state === 'failed'
            ? item.reason
            : humanSize(item.file.size);
      meta.append(name, sub);
      li.append(meta);

      if (item.state === 'failed') {
        const again = document.createElement('button');
        again.type = 'button';
        again.className = 'act';
        again.textContent = 'Try again';
        again.setAttribute('aria-label', `Try ${item.file.name || 'that file'} again`);
        again.addEventListener('click', () => this.retry(item));
        li.append(again);
      }

      // Present in EVERY state, uploading included: cancelling a slow upload is the whole
      // reason busy must not disable the row.
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'act';
      rm.textContent = '×';
      rm.setAttribute('aria-label', `Remove ${item.file.name || 'attachment'}`);
      rm.addEventListener('click', () => this.dropItem(item));
      li.append(rm);

      this.listEl.append(li);
    }
  }

  private sync(): void {
    this.labelEl.textContent = this.getAttribute('label') ?? '';
    this.reqEl.hidden = !this.hasAttribute('required');
    // CAOS-1697 — `label-hidden` draws no visible caption and changes NOTHING else. `label` is
    // still required and still read, twice and independently: once for the span above and once for
    // the drop target's `aria-label` below, which is why hiding the span costs nothing in assistive
    // technology and needs no second string from the caller.
    //
    // The whole `<label>` is hidden rather than the span inside it. `.label:empty` cannot do this
    // job: the element always has two span children, so it is never `:empty` no matter what the
    // text is, and a blank caption would still take its share of the control's flex gap.
    //
    // `hidden` means hidden in here because `base-element.ts` adopts `[hidden] { display: none
    // !important }` into every shadow root (CAOS-581). Without that it would not: the browser's own
    // `[hidden]` rule is a user-agent rule and any author `display` outranks it, which is how a
    // control once stayed on screen and clickable while reporting `hidden === true`.
    this.labelWrap.hidden = this.hasAttribute('label-hidden');
    this.input.accept = this.getAttribute('accept') ?? '';
    this.input.multiple = this.hasAttribute('multiple');

    const disabled = this.isDisabled;
    this.input.disabled = disabled;
    this.dropzone.setAttribute('aria-disabled', String(disabled));
    this.dropzone.tabIndex = disabled ? -1 : 0;

    const label = this.getAttribute('label');
    this.dropzone.setAttribute('aria-label', label ? `${label} — choose files` : 'Choose files');
    this.hint.textContent = this.hasAttribute('multiple')
      ? 'Choose files, or drop or paste them here'
      : 'Choose a file, or drop or paste it here';

    if (this.hasAttribute('invalid')) this.dropzone.setAttribute('aria-invalid', 'true');
    else this.dropzone.removeAttribute('aria-invalid');
    if (this.hasAttribute('required')) this.dropzone.setAttribute('aria-required', 'true');
    else this.dropzone.removeAttribute('aria-required');

    const message = this.getAttribute('message');
    if (message !== null) this.messageEl.textContent = message;
  }
}

define('caos-file-field', CaosFileField);

declare global {
  interface HTMLElementTagNameMap {
    'caos-file-field': CaosFileField;
  }
}
